import apiService from '../services/apiService';
import Bug from '../models/Bug';

export const bugController = {
  // Get all bugs with optional filters
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `bugs?${queryParams}` : 'bugs';
      const response = await apiService.get(endpoint);
      const items = response.data?.map(item => Bug.fromJSON(item)) || [];
      return { success: true, data: items, total: response.total || items.length };
    } catch (error) {
      return { success: false, data: [], message: error.message || 'Failed to fetch bugs' };
    }
  },

  // Get single bug by ID
  getById: async (id) => {
    try {
      const response = await apiService.get(`bugs/${id}`);
      return { success: true, data: Bug.fromJSON(response.data) };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  },

  // Get bugs by project
  getByProject: async (projectId) => {
    try {
      const response = await apiService.get(`bugs?projectId=${projectId}`);
      const items = response.data?.map(item => Bug.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Get bugs by task
  getByTask: async (taskId) => {
    try {
      const response = await apiService.get(`bugs?taskId=${taskId}`);
      const items = response.data?.map(item => Bug.fromJSON(item)) || [];
      return { success: true, data: items };
    } catch (error) {
      return { success: false, data: [], message: error.message };
    }
  },

  // Create new bug
  create: async (data) => {
    try {
      const item = new Bug(data);
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.post('bugs', item.toJSON());
      return { 
        success: true, 
        data: Bug.fromJSON(response.data), 
        message: 'Bug reported successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update existing bug
  update: async (id, data) => {
    try {
      const item = new Bug({ ...data, id });
      const validation = item.validate();
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }
      const response = await apiService.put(`bugs/${id}`, item.toJSON());
      return { 
        success: true, 
        data: Bug.fromJSON(response.data), 
        message: 'Bug updated successfully' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update bug status
  updateStatus: async (id, status) => {
    try {
      const updateData = { status };
      if (status === 'Resolved' || status === 'Closed') {
        updateData.resolvedDate = new Date().toISOString();
      }
      const response = await apiService.patch(`bugs/${id}/status`, updateData);
      return { 
        success: true, 
        data: Bug.fromJSON(response.data), 
        message: 'Status updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update bug severity
  updateSeverity: async (id, severity) => {
    try {
      const response = await apiService.patch(`bugs/${id}/severity`, { severity });
      return { 
        success: true, 
        data: Bug.fromJSON(response.data), 
        message: 'Severity updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Update bug priority
  updatePriority: async (id, priority) => {
    try {
      const response = await apiService.patch(`bugs/${id}/priority`, { priority });
      return { 
        success: true, 
        data: Bug.fromJSON(response.data), 
        message: 'Priority updated' 
      };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Delete bug
  delete: async (id) => {
    try {
      await apiService.delete(`bugs/${id}`);
      return { success: true, message: 'Bug deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk update bugs
  bulkUpdate: async (bugIds, updates) => {
    try {
      const response = await apiService.post('bugs/bulk-update', { bugIds, updates });
      return { success: true, data: response.data, message: 'Bugs updated successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Bulk delete bugs
  bulkDelete: async (bugIds) => {
    try {
      await apiService.post('bugs/bulk-delete', { bugIds });
      return { success: true, message: 'Bugs deleted successfully' };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Export bugs
  export: async (filters = {}, format = 'csv') => {
    try {
      const queryParams = new URLSearchParams({ ...filters, format }).toString();
      const response = await apiService.get(`bugs/export?${queryParams}`);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, message: error.message };
    }
  },

  // Get bug statistics
  getStatistics: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams(filters).toString();
      const endpoint = queryParams ? `bugs/statistics?${queryParams}` : 'bugs/statistics';
      const response = await apiService.get(endpoint);
      return { success: true, data: response.data };
    } catch (error) {
      return { success: false, data: null, message: error.message };
    }
  }
};

export default bugController;